import React, { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ClipboardList, Plus, Eye, Trash2, Edit, Upload, X, Building2 } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Modal } from '../../components/ui/Modal';
import { ConfirmDeleteModal } from '../../components/ui/ConfirmDeleteModal';
import { Table, Column } from '../../components/ui/Table';
import { Pagination } from '../../components/ui/Pagination';
import { api, getPropertyCoverImageUrl, getUploadsPublicUrl, PROPERTY_IMAGE_PLACEHOLDER } from '../../services/api';
import { PropertySelect } from '../../components/ui/PropertySelect';
import { Property } from '../../types';
import { usePermissions } from '../../hooks/usePermissions';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { isMaintenanceRoleName } from '../../utils/investorRole';
import { formatDate } from '../../utils/date';

type ReportStatus = 'pending' | 'in_progress' | 'reviewed' | 'resolved';

interface ReportRow {
  id: string;
  propertyId: string;
  unitId?: string | null;
  reportedById?: string;
  reportedByName?: string;
  propertyName?: string;
  propertyCoverImage?: string;
  unitNumber?: string;
  title: string;
  findings: string;
  images: string[];
  status: ReportStatus;
  notes?: string | null;
  createdAt?: string;
}

const limit = 20;

function StatusBadge({ status }: { status: ReportStatus }) {
  const config: Record<ReportStatus, { label: string; className: string }> = {
    pending: { label: 'Pendiente', className: 'bg-amber-100 text-amber-800' },
    in_progress: { label: 'En curso', className: 'bg-sky-100 text-sky-800' },
    reviewed: { label: 'Revisado', className: 'bg-blue-100 text-blue-800' },
    resolved: { label: 'Resuelto', className: 'bg-green-100 text-green-800' },
  };
  const { label, className } = config[status] ?? config.pending;
  return <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${className}`}>{label}</span>;
}

function mapApiReportToRow(item: any): ReportRow {
  const prop = item.properties ?? item.property;
  const unit = item.units ?? item.unit;
  return {
    id: item.id,
    propertyId: item.propertyId,
    unitId: item.unitId,
    reportedById: item.reportedBy,
    reportedByName: item.users?.name,
    propertyName: prop?.name,
    propertyCoverImage: prop?.coverImage,
    unitNumber: unit?.unitNumber ?? item.unitNumber,
    title: item.title,
    findings: item.findings,
    images: Array.isArray(item.images) ? item.images : [],
    status: (item.status || 'pending') as ReportStatus,
    notes: item.notes ?? null,
    createdAt: item.createdAt,
  };
}

export const MaintenanceReports: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { hasPermission } = usePermissions();
  const canDeleteReports = hasPermission('delete_maintenance_reports');
  const canCreateReports =
    hasPermission('create_maintenance_reports') || hasPermission('manage_maintenance_reports');
  const canUpdateReports =
    hasPermission('update_maintenance_reports') || hasPermission('manage_maintenance_reports');
  const { user } = useAuth();
  const isMaintenanceTechnician = isMaintenanceRoleName(user?.role?.name);
  const roleName = user?.role?.name?.toLowerCase() ?? '';
  const isAdminOrDireccion =
    roleName === 'administrador' || roleName === 'admin' || roleName === 'dirección' || roleName === 'direccion';

  const [page, setPage] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selected, setSelected] = useState<ReportRow | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [form, setForm] = useState({
    propertyId: '',
    unitId: '',
    title: '',
    findings: '',
    status: 'pending' as ReportStatus,
    notes: '',
  });
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [pendingPreviews, setPendingPreviews] = useState<string[]>([]);
  const [uploadedUrls, setUploadedUrls] = useState<string[]>([]);
  const [reporterFilter, setReporterFilter] = useState<string>('');
  const [reportPendingDelete, setReportPendingDelete] = useState<{ id: string; title: string } | null>(null);

  const { data: reportsRes, isLoading: loading, error: queryError } = useQuery({
    queryKey: ['maintenance-reports', page],
    queryFn: () => api.getMaintenanceReports({ page, limit }),
    staleTime: 60_000,
  });

  const { data: propertiesRes } = useQuery({
    queryKey: ['properties', 'list'],
    queryFn: () => api.getProperties({ limit: 500 }),
    staleTime: 60_000,
    enabled: isModalOpen,
  });

  const { data: unitsData } = useQuery({
    queryKey: ['units', form.propertyId],
    queryFn: () => api.getUnits(form.propertyId),
    staleTime: 60_000,
    enabled: !!form.propertyId && isModalOpen,
  });

  const reports: ReportRow[] = useMemo(() => {
    const list = reportsRes?.data ?? [];
    return Array.isArray(list) ? list.map(mapApiReportToRow) : [];
  }, [reportsRes]);
  const reporters = useMemo(() => {
    const map = new Map<string, string>();
    reports.forEach((r) => {
      if (r.reportedById && r.reportedByName) {
        map.set(r.reportedById, r.reportedByName);
      }
    });
    return Array.from(map, ([id, name]) => ({ id, name }));
  }, [reports]);
  const visibleReports: ReportRow[] = useMemo(
    () => reports.filter((r) => !reporterFilter || r.reportedById === reporterFilter),
    [reports, reporterFilter],
  );
  const total = reportsRes?.total ?? 0;
  const totalPages = reportsRes?.totalPages ?? 0;
  const error =
    queryError != null ? (queryError instanceof Error ? queryError.message : String(queryError)) : null;

  const uploadMutation = useMutation({
    mutationFn: (files: File[]) => api.uploadMaintenanceReportImages(files),
    onSuccess: (res) => {
      setUploadedUrls((prev) => [...prev, ...(res.fileUrls ?? [])]);
    },
    onError: (err: Error) => setFormError(err.message || 'Error al subir imágenes'),
  });

  const createMutation = useMutation({
    mutationFn: async () => {
      let images = uploadedUrls;
      if (pendingFiles.length > 0) {
        const res = await api.uploadMaintenanceReportImages(pendingFiles);
        images = [...images, ...(res.fileUrls ?? [])];
      }
      return api.createMaintenanceReport({
        propertyId: form.propertyId,
        unitId: form.unitId || undefined,
        title: form.title,
        findings: form.findings,
        status: form.status,
        notes: form.notes || undefined,
        images,
      });
    },
    onSuccess: (created) => {
      if (created?.id) {
        queryClient.setQueryData(['maintenance-report', created.id], created);
      }
      queryClient.invalidateQueries({ queryKey: ['maintenance-reports'] });
      setIsModalOpen(false);
      setPage(1);
    },
    onError: (err: Error) => setFormError(err.message || 'Error al guardar'),
  });

  const updateMutation = useMutation({
    mutationFn: async () => {
      if (!selected) throw new Error('No hay reporte seleccionado');
      let images = uploadedUrls;
      if (pendingFiles.length > 0) {
        const res = await api.uploadMaintenanceReportImages(pendingFiles);
        images = [...images, ...(res.fileUrls ?? [])];
      }
      return api.updateMaintenanceReport(selected.id, {
        propertyId: form.propertyId,
        unitId: form.unitId || null,
        title: form.title,
        findings: form.findings,
        status: form.status,
        notes: form.notes || null,
        images,
      });
    },
    onSuccess: (updated) => {
      if (updated?.id) {
        queryClient.setQueryData(['maintenance-report', updated.id], (old: unknown) => {
          const prev = old && typeof old === 'object' ? (old as Record<string, unknown>) : null;
          if (!prev) return updated;
          return { ...prev, ...updated } as typeof updated;
        });
      }
      queryClient.invalidateQueries({ queryKey: ['maintenance-reports'] });
      setIsModalOpen(false);
    },
    onError: (err: Error) => setFormError(err.message || 'Error al actualizar'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deleteMaintenanceReport(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['maintenance-reports'] }),
  });

  const openCreate = () => {
    setSelected(null);
    setFormError(null);
    setForm({ propertyId: '', unitId: '', title: '', findings: '', status: 'pending', notes: '' });
    setPendingFiles([]);
    setPendingPreviews([]);
    setUploadedUrls([]);
    setIsModalOpen(true);
  };

  const openEdit = (row: ReportRow) => {
    setSelected(row);
    setFormError(null);
    setForm({
      propertyId: row.propertyId,
      unitId: row.unitId || '',
      title: row.title,
      findings: row.findings,
      status: row.status,
      notes: row.notes || '',
    });
    setPendingFiles([]);
    setPendingPreviews([]);
    setUploadedUrls(row.images || []);
    setIsModalOpen(true);
  };

  const openDetail = (row: ReportRow) => {
    navigate(`/maintenance/report/${row.id}`);
  };

  const onFilesSelected = (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const next: File[] = [];
    const previews: string[] = [];
    Array.from(files).forEach((f) => {
      if (!f.type.startsWith('image/')) return;
      next.push(f);
      previews.push(URL.createObjectURL(f));
    });
    setPendingFiles((prev) => [...prev, ...next]);
    setPendingPreviews((prev) => [...prev, ...previews]);
  };

  const removePending = (idx: number) => {
    const url = pendingPreviews[idx];
    if (url) URL.revokeObjectURL(url);
    setPendingFiles((prev) => prev.filter((_, i) => i !== idx));
    setPendingPreviews((prev) => prev.filter((_, i) => i !== idx));
  };

  const removeUploaded = (idx: number) => {
    setUploadedUrls((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.propertyId || !form.title.trim() || !form.findings.trim()) {
      setFormError('Completa propiedad, título y hallazgos.');
      return;
    }
    if (selected) updateMutation.mutate();
    else createMutation.mutate();
  };

  const columns: Column<ReportRow>[] = [
    {
      header: 'Propiedad',
      accessor: (row) => (
        <div className="flex items-center gap-3">
          <img
            src={row.propertyCoverImage ? getPropertyCoverImageUrl(row.propertyCoverImage) : PROPERTY_IMAGE_PLACEHOLDER}
            alt=""
            className="w-10 h-10 rounded object-cover flex-shrink-0"
          />
          <div>
            <div className="font-medium text-gray-900">{row.propertyName || '—'}</div>
            <div className="text-xs text-gray-500">{row.unitNumber ? `Unidad ${row.unitNumber}` : '—'}</div>
          </div>
        </div>
      ),
    },
    { header: 'Título', accessor: (row) => row.title },
    ...(isAdminOrDireccion
      ? [
          {
            header: 'Reportado por',
            accessor: (row: ReportRow) => row.reportedByName || '—',
          } as Column<ReportRow>,
        ]
      : []),
    {
      header: 'Fecha',
      accessor: (row) => (row.createdAt ? formatDate(row.createdAt) : '—'),
    },
    { header: 'Estado', accessor: (row) => <StatusBadge status={row.status} /> },
    {
      header: '',
      accessor: (row) => (
        <div className="flex items-center gap-2 justify-end">
          <button
            type="button"
            onClick={() => openDetail(row)}
            className="inline-flex items-center justify-center p-2 rounded-lg text-sm font-medium bg-white text-primary border border-gray-200 hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
            title="Ver reporte"
            aria-label="Ver reporte"
          >
            <Eye className="w-4 h-4" />
          </button>
          {canUpdateReports && !isMaintenanceTechnician && (
            <>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => openEdit(row)}
                className="!p-2 min-w-0"
                title="Editar reporte"
                aria-label="Editar reporte"
              >
                <Edit className="w-4 h-4" aria-hidden="true" />
              </Button>
              {canDeleteReports && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setReportPendingDelete({ id: row.id, title: row.title })}
                  disabled={deleteMutation.isPending && reportPendingDelete?.id === row.id}
                  className="!p-2 min-w-0 text-red-600 hover:bg-red-50"
                  title="Eliminar reporte"
                  aria-label="Eliminar reporte"
                >
                  <Trash2 className="w-4 h-4" aria-hidden="true" />
                </Button>
              )}
            </>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <ClipboardList className="w-7 h-7 text-primary" />
          Reportes de mantenimiento
        </h1>
        {canCreateReports && !isMaintenanceTechnician && (
          <Button onClick={openCreate} className="flex items-center gap-2">
            <Plus className="w-4 h-4" /> Generar reporte
          </Button>
        )}
      </div>

      <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
        {error && <div className="mx-4 mt-3 p-3 bg-red-50 text-red-700 rounded-lg text-sm">{error}</div>}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : (
          <>
            {isAdminOrDireccion && reporters.length > 0 && (
              <div className="px-4 pt-3 flex flex-wrap items-center gap-3">
                <label className="text-sm text-gray-700">
                  Reportado por:
                  <select
                    value={reporterFilter}
                    onChange={(e) => setReporterFilter(e.target.value)}
                    className="ml-2 rounded-lg border border-gray-300 px-3 py-1.5 text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary"
                  >
                    <option value="">Todos</option>
                    {reporters.map((r) => (
                      <option key={r.id} value={r.id}>
                        {r.name}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
            )}
            <Table data={visibleReports} columns={columns} />
            {totalPages > 1 && (
              <Pagination page={page} totalPages={totalPages} total={total} limit={limit} onPageChange={setPage} />
            )}
          </>
        )}
      </div>

      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title={selected ? 'Editar reporte' : 'Generar reporte'} size="lg">
        <form onSubmit={handleSubmit} className="space-y-4">
          {formError && <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm">{formError}</div>}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Propiedad *</label>
            <PropertySelect
              properties={(propertiesRes?.data ?? []) as Property[]}
              value={form.propertyId}
              onChange={(propertyId) => setForm((d) => ({ ...d, propertyId, unitId: '' }))}
              placeholder="Seleccione una propiedad"
              showPropertyCost={false}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Unidad</label>
            <select
              value={form.unitId}
              onChange={(e) => setForm((d) => ({ ...d, unitId: e.target.value }))}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-primary/20 focus:border-primary"
              disabled={!form.propertyId}
            >
              <option value="">(Opcional) Selecciona una unidad</option>
              {(Array.isArray(unitsData) ? unitsData : []).map((u: any) => (
                <option key={u.id} value={u.id}>
                  {u.unitNumber}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Título *</label>
            <input
              value={form.title}
              onChange={(e) => setForm((d) => ({ ...d, title: e.target.value }))}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-primary/20 focus:border-primary"
              placeholder="Ej. Fuga en baño"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">¿Qué encontraste? *</label>
            <textarea
              value={form.findings}
              onChange={(e) => setForm((d) => ({ ...d, findings: e.target.value }))}
              rows={4}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-primary/20 focus:border-primary"
              required
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Estado</label>
              <select
                value={form.status}
                onChange={(e) => setForm((d) => ({ ...d, status: e.target.value as ReportStatus }))}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-primary/20 focus:border-primary"
              >
                <option value="pending">Pendiente</option>
                <option value="in_progress">En curso</option>
                <option value="reviewed">Revisado</option>
                <option value="resolved">Resuelto</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notas</label>
              <input
                value={form.notes}
                onChange={(e) => setForm((d) => ({ ...d, notes: e.target.value }))}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-primary/20 focus:border-primary"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Fotografías</label>
            <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-4">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 text-sm text-gray-700">
                  <Upload className="w-4 h-4 text-gray-400" />
                  <span>Sube múltiples imágenes (JPG/PNG)</span>
                </div>
                <label className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white border border-gray-300 text-sm cursor-pointer hover:bg-gray-50">
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    onChange={(e) => onFilesSelected(e.target.files)}
                  />
                  <Plus className="w-4 h-4" />
                  Añadir
                </label>
              </div>
              {(uploadedUrls.length > 0 || pendingPreviews.length > 0) && (
                <div className="mt-4 grid grid-cols-3 sm:grid-cols-4 gap-3">
                  {uploadedUrls.map((url, i) => (
                    <div key={`u-${i}`} className="relative">
                      <img src={getUploadsPublicUrl(url)} alt="" className="w-full h-20 object-cover rounded-lg border border-gray-200" />
                      <button
                        type="button"
                        onClick={() => removeUploaded(i)}
                        className="absolute -top-2 -right-2 p-1 bg-red-600 text-white rounded-full hover:bg-red-700"
                        title="Quitar"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                  {pendingPreviews.map((url, i) => (
                    <div key={`p-${i}`} className="relative">
                      <img src={url} alt="" className="w-full h-20 object-cover rounded-lg border border-gray-200" />
                      <button
                        type="button"
                        onClick={() => removePending(i)}
                        className="absolute -top-2 -right-2 p-1 bg-red-600 text-white rounded-full hover:bg-red-700"
                        title="Quitar"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="secondary" onClick={() => setIsModalOpen(false)}>
              Cancelar
            </Button>
            <Button type="submit" disabled={createMutation.isPending || updateMutation.isPending}>
              {selected ? (updateMutation.isPending ? 'Guardando...' : 'Guardar') : createMutation.isPending ? 'Guardando...' : 'Guardar reporte'}
            </Button>
          </div>
        </form>
      </Modal>

      <ConfirmDeleteModal
        isOpen={reportPendingDelete !== null}
        onClose={() => {
          if (!deleteMutation.isPending) setReportPendingDelete(null);
        }}
        onConfirm={() => {
          if (!reportPendingDelete) return;
          deleteMutation.mutate(reportPendingDelete.id, {
            onSuccess: () => setReportPendingDelete(null),
            onError: () => setReportPendingDelete(null),
          });
        }}
        message={
          reportPendingDelete ? (
            <>
              ¿Seguro que deseas eliminar el reporte <strong>{reportPendingDelete.title}</strong>?
            </>
          ) : null
        }
        isLoading={deleteMutation.isPending}
      />
    </div>
  );
};

